import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { WidgetSync } from "@/plugins/widget-sync";
import NativeNotification from "@/plugins/native-notification";

export type PushNotificationPlatform = "ios" | "android";

export interface PushNotificationState {
  isSupported: boolean;
  isRegistered: boolean;
  token: string | null;
  platform: PushNotificationPlatform | null;
}

// Check if push notifications are supported on this platform
export function isPushSupported(): boolean {
  return Capacitor.isNativePlatform();
}

// Get the current platform
export function getPlatform(): PushNotificationPlatform | null {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "ios";
  if (platform === "android") return "android";
  return null;
}

// Check current permission status
export async function checkPermissions(): Promise<"prompt" | "granted" | "denied"> {
  if (!isPushSupported()) {
    return "denied";
  }

  const result = await PushNotifications.checkPermissions();
  // Map Capacitor's permission state to our simpler type
  if (result.receive === "granted") return "granted";
  if (result.receive === "denied") return "denied";
  return "prompt";
}

// Request push notification permissions
export async function requestPermissions(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  const result = await PushNotifications.requestPermissions();
  return result.receive === "granted";
}

// Register for push notifications and get FCM token
export async function registerForPush(): Promise<string | null> {
  if (!isPushSupported()) {
    console.log("[Push] Not supported on this platform");
    return null;
  }

  console.log("[Push] Requesting permissions...");
  const permissionGranted = await requestPermissions();
  if (!permissionGranted) {
    console.log("[Push] Permission denied");
    return null;
  }
  console.log("[Push] Permission granted, registering...");

  // Register with APNs first
  await PushNotifications.register();
  console.log("[Push] APNs registration triggered");

  // On iOS, get FCM token from app group UserDefaults (stored by AppDelegate)
  // Need to poll since token arrives asynchronously after APNs registration
  const platform = getPlatform();
  if (platform === "ios") {
    console.log("[Push] Waiting for FCM token from WidgetSync...");

    // Poll for up to 10 seconds
    for (let attempt = 1; attempt <= 20; attempt++) {
      try {
        const { token } = await WidgetSync.getFCMToken();
        if (token) {
          console.log("[Push] FCM token received:", token.substring(0, 20) + "...");
          return token;
        }
      } catch (error) {
        console.log(`[Push] Attempt ${attempt}/20 - error:`, error);
      }
      console.log(`[Push] Attempt ${attempt}/20 - token not ready yet`);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    console.error("[Push] FCM token not available after 10s");
    return null;
  }

  // On Android, use the standard Capacitor registration flow
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log("[Push] Registration timeout after 10s");
      resolve(null);
    }, 10000);

    PushNotifications.addListener("registration", (token) => {
      clearTimeout(timeout);
      console.log("[Push] Registration success, token:", token.value.substring(0, 20) + "...");
      resolve(token.value);
    });

    PushNotifications.addListener("registrationError", (error) => {
      clearTimeout(timeout);
      console.error("[Push] Registration error:", error);
      resolve(null);
    });
  });
}

// Unregister from push notifications
export async function unregisterFromPush(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }

  await PushNotifications.removeAllListeners();
}

// Clear app badge and remove delivered notifications
export async function clearBadge(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }

  try {
    // Use our native plugin which doesn't require Capacitor push registration
    await NativeNotification.clearBadge();
  } catch {
    // Silently fail - badge clearing is not critical
  }
}

// Set up notification received listener (foreground)
export function onNotificationReceived(
  callback: (notification: { title?: string; body?: string; data?: Record<string, unknown> }) => void
): () => void {
  if (!isPushSupported()) {
    return () => {};
  }

  const listener = PushNotifications.addListener("pushNotificationReceived", (notification) => {
    callback({
      title: notification.title,
      body: notification.body,
      data: notification.data,
    });
  });

  return () => {
    listener.then((l) => l.remove());
  };
}

// Set up notification action listener (tapped)
export function onNotificationTapped(
  callback: (notification: { data?: Record<string, unknown> }) => void
): () => void {
  if (!isPushSupported()) {
    return () => {};
  }

  const listener = PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (notification) => {
      callback({
        data: notification.notification.data,
      });
    }
  );

  return () => {
    listener.then((l) => l.remove());
  };
}
